"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchLocation, fetchLocations, fetchTenantSettings } from "@/lib/api";
import { GoogleMapEmbed } from "@/components/GoogleMapEmbed";
import type { LocationDto } from "@/types/page";

export function MapBlock({ content }: { content: Record<string, unknown> }) {
  const heading = String(content.heading ?? "Visit us");
  const showAll = Boolean(content.showAll ?? true);
  const locationSlug = String(content.locationSlug ?? "");
  const [locations, setLocations] = useState<LocationDto[]>([]);

  const { data: tenant } = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: fetchTenantSettings,
  });
  const apiKey = tenant?.settings?.googleMapsApiKey;

  useEffect(() => {
    if (!showAll && locationSlug) {
      fetchLocation(locationSlug).then((location) => setLocations(location ? [location] : [])).catch(() => setLocations([]));
      return;
    }
    fetchLocations().then(setLocations).catch(() => setLocations([]));
  }, [showAll, locationSlug]);

  const firstWithGeo = useMemo(
    () => locations.find((location) => location.latitude != null && location.longitude != null),
    [locations],
  );
  const mapUrl = firstWithGeo
    ? `https://www.google.com/maps/search/?api=1&query=${firstWithGeo.latitude},${firstWithGeo.longitude}`
    : locations[0]
      ? mapsUrl(locations[0])
      : "";

  if (!locations.length) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 py-10">
      {heading && <h2 className="mb-6 text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <div className="grid gap-3">
          {locations.map((location) => (
            <div key={location.id} className="rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <h3 className="font-semibold text-[var(--color-text)]">{location.name}</h3>
              {location.description && <p className="mt-1 text-sm text-slate-600">{location.description}</p>}
              <p className="mt-3 text-sm text-slate-700">
                {location.addressLine1}
                {location.addressLine2 ? `, ${location.addressLine2}` : ""}
                <br />
                {location.city}, {location.region} {location.postalCode}
              </p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                {location.phone && <a className="text-[var(--color-primary)] hover:underline" href={`tel:${location.phone}`}>{location.phone}</a>}
                <a className="text-[var(--color-primary)] hover:underline" href={`/locations/${location.slug}`}>Details</a>
                <a className="text-[var(--color-primary)] hover:underline" href={mapsUrl(location)}>Directions</a>
              </div>
            </div>
          ))}
        </div>
        {apiKey && locations[0] ? (
          <GoogleMapEmbed apiKey={apiKey} location={locations[0]} />
        ) : locations[0] ? (
          <div className="overflow-hidden rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)]">
            <OsmStaticPreview location={locations[0]} />
            <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3 text-sm">
              <span className="text-slate-600">Map preview</span>
              <a className="font-medium text-[var(--color-primary)] hover:underline" href={mapUrl}>
                Open directions
              </a>
            </div>
          </div>
        ) : (
          <a
            href={mapUrl}
            className="flex min-h-[320px] items-center justify-center rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-6 text-center text-sm font-medium text-[var(--color-primary)]"
          >
            Open map and directions
          </a>
        )}
      </div>
    </section>
  );
}

function mapsUrl(location: LocationDto): string {
  const query = [location.addressLine1, location.city, location.region, location.postalCode]
    .filter(Boolean)
    .join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}


function OsmStaticPreview({ location }: { location: LocationDto }) {
  const preview = osmTilePreview(location);

  if (!preview) {
    return (
      <div className="flex h-[320px] items-center justify-center bg-[var(--color-surface-muted)] px-4 text-center text-sm text-slate-600">
        Map preview unavailable. Use Open directions for the live map.
      </div>
    );
  }

  return (
    <div
      className="relative h-[320px] w-full overflow-hidden bg-[#d9e6cf]"
      aria-label={`Map preview for ${location.name}`}
      role="img"
    >
      <div className="absolute left-1/2 top-1/2 h-[360px] w-[640px] -translate-x-1/2 -translate-y-1/2">
        {preview.tiles.map((tile) => (
          <img
            key={`${tile.x}-${tile.y}`}
            src={`https://tile.openstreetmap.org/${preview.zoom}/${tile.x}/${tile.y}.png`}
            alt=""
            className="absolute h-64 w-64 select-none"
            style={{ left: tile.left, top: tile.top }}
            loading="lazy"
            draggable={false}
          />
        ))}
        <div
          className="absolute h-5 w-5 -translate-x-1/2 -translate-y-full rounded-full border-2 border-white bg-red-600 shadow-lg after:absolute after:left-1/2 after:top-[14px] after:h-3 after:w-3 after:-translate-x-1/2 after:rotate-45 after:bg-red-600"
          style={{ left: preview.markerLeft, top: preview.markerTop }}
          aria-hidden="true"
        />
      </div>
      <div className="absolute bottom-1 right-1 rounded bg-white/85 px-1.5 py-0.5 text-[10px] text-slate-600">
        © OpenStreetMap contributors
      </div>
    </div>
  );
}

function osmTilePreview(location: LocationDto) {
  if (location.latitude == null || location.longitude == null) return null;

  const lat = Number(location.latitude);
  const lon = Number(location.longitude);
  const zoom = 15;
  const tileSize = 256;
  const width = 640;
  const height = 360;
  const scale = tileSize * 2 ** zoom;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const worldX = ((lon + 180) / 360) * scale;
  const worldY = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  const leftEdge = worldX - width / 2;
  const topEdge = worldY - height / 2;
  const startX = Math.floor(leftEdge / tileSize);
  const endX = Math.floor((leftEdge + width) / tileSize);
  const startY = Math.floor(topEdge / tileSize);
  const endY = Math.floor((topEdge + height) / tileSize);
  const maxTile = 2 ** zoom;
  const tiles: Array<{ x: number; y: number; left: number; top: number }> = [];

  for (let x = startX; x <= endX; x += 1) {
    for (let y = startY; y <= endY; y += 1) {
      if (y < 0 || y >= maxTile) continue;
      tiles.push({
        x: ((x % maxTile) + maxTile) % maxTile,
        y,
        left: x * tileSize - leftEdge,
        top: y * tileSize - topEdge,
      });
    }
  }

  return { tiles, zoom, markerLeft: width / 2, markerTop: height / 2 };
}
